import {
  CalendarDaysIcon,
  ClipboardListIcon,
  LayersIcon,
  LayoutGridIcon,
  ShieldIcon,
  TargetIcon,
  TrophyIcon,
  UsersIcon,
  UsersRoundIcon,
} from 'lucide-react'
import type {
  Bonus,
  ComparisonRow,
  Faq,
  IconStat,
  Module,
  Purchase,
  StatItem,
  Testimonial,
} from '../types/landing'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/pKYWB16CRE5vZijq2b4Smz/5580018b-2612-416e-9d78-c5c0f623853e.webp'

export const CHECKOUT_URL = 'https://ready-tennis.impultienda.ar/checkout'

export const heroHighlights = [
  '6 Volumes',
  '5 Bonuses',
  '360+ Sessions',
  '120+ Drills',
  'All Levels',
]

export const proofStats: StatItem[] = [
  { value: '500+', label: 'Tennis Coaches' },
  { value: '6 + 5', label: 'Volumes & Bonuses' },
  { value: '4.9/5', label: 'Average rating' },
  { value: 'Worldwide', label: 'Coaching authority' },
]

export const proofNotes: Omit<Testimonial, 'initials'>[] = [
  {
    quote:
      'I mapped out my entire academy year in a single evening. Now every session has a clear tactical objective and my players are actually showing real progress.',
    name: 'Marc R.',
    role: 'Academy Head Coach, Spain',
  },
  {
    quote:
      'I no longer spend hours on YouTube hunting for drills. I open Tennis Ready™ and it’s all there. Everything. Ready to go.',
    name: 'Sarah L.',
    role: 'Teaching Pro, USA',
  },
  {
    quote:
      'My group lessons finally flow. Rotations, stations, feeding patterns — everything is organized and the courts never sit idle again.',
    name: 'Andrea P.',
    role: 'High Performance Coach, Italy',
  },
]

export const problems = [
  'Spending hours every week planning sessions from scratch',
  'Running out of drills and repeating the same dead-ball exercises',
  'Struggling to keep players engaged and motivated during group lessons',
  'Not knowing how to structure a logical skill progression for different levels',
  'Feeling unprepared and improvising 5 minutes before walking onto the court',
  'Having no system to evaluate player development and report progress to parents',
]

export const systemStats: IconStat[] = [
  { icon: CalendarDaysIcon, value: '300+', label: 'Pages' },
  { icon: ClipboardListIcon, value: '360+', label: 'Sessions' },
  { icon: UsersIcon, value: '120+', label: 'Drills & Progressions' },
  { icon: ShieldIcon, value: 'All', label: 'Levels & Categories' },
]

export const categoryTags = [
  'Beginner (9th Category)',
  'Green Ball (7th–8th)',
  'Intermediate (5th–6th)',
  'Advanced (3rd–4th)',
  'High Performance (1st–2nd)',
  'All Surfaces & Formats',
]

export const modules: Module[] = [
  {
    icon: TargetIcon,
    kicker: 'Volume 01',
    title: 'Core Drills Academy',
    description:
      '50 foundation sessions for rhythm, ball control, and feeding patterns mastery.',
    features: 'Rhythm · Ball Control · Feeding Patterns · Consistency Ladders',
    accentClass: 'bg-emerald-600',
  },
  {
    icon: LayersIcon,
    kicker: 'Volume 02',
    title: 'Technical Masterclass',
    description:
      '50 complete sessions focused on stroke mechanics, footwork, and technical cues.',
    features: 'Stroke Mechanics · Footwork · Serve & Return · Technical Cues',
    accentClass: 'bg-sky-600',
  },
  {
    icon: ClipboardListIcon,
    kicker: 'Volume 03',
    title: 'Session Planner System',
    description:
      'The ultimate library of weekly plans and logical progressions for all levels.',
    features: 'Weekly Plans · Level Progressions · Warm-Ups · Session Templates',
    accentClass: 'bg-gold',
  },
  {
    icon: TrophyIcon,
    kicker: 'Volume 04',
    title: 'Match Play & Tactical Games',
    description:
      'Tactical patterns, point-play scenarios, and competitive games that players love.',
    features: 'Tactical Patterns · Point Play · Scenarios · Competitive Games',
    accentClass: 'bg-red-500',
  },
  {
    icon: LayoutGridIcon,
    kicker: 'Volume 05',
    title: 'Group Coaching Blueprint',
    description:
      'Master rotations, stations, and flow. Run large groups with 100% efficiency.',
    features: 'Rotations · Stations · Court Flow · Large-Group Management',
    accentClass: 'bg-violet-600',
  },
  {
    icon: UsersRoundIcon,
    kicker: 'Volume 06',
    title: 'Full-Year Coaching Framework',
    description:
      'Annual calendars, unit maps, and season blocks to structure your entire year.',
    features: 'Annual Calendar · Unit Maps · Season Blocks · Periodization',
    accentClass: 'bg-teal-600',
  },
]

export const bonuses: Bonus[] = [
  {
    title: 'The Emergency Lesson Pack',
    value: '$80 value',
    description:
      '30 ready-to-teach sessions for when you have zero time to plan. Grab a page. Walk on court confident.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/71b464ec-d18b-4472-aa6a-c0a62012e4ea.jpg',
  },
  {
    title: 'The Rain-Delay / Indoor Drill Vault',
    value: '$100 value',
    description:
      'High-energy drills and theory sessions for limited spaces or rain days. Never lose a booking again.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ab162d1b-25fd-4268-b2ac-2bb803ec1520.jpg',
  },
  {
    title: 'The Player Assessment Toolkit',
    value: '$70 value',
    description:
      'Plug-and-play evaluation sheets to track progress and impress parents without drowning in paperwork.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c0a3d405-68b1-4730-ad23-574ae32ecec2.jpg',
  },
  {
    title: 'The Yearly Academy Calendar',
    value: '$90 value',
    description:
      'Your entire season structured from Week 1 to Week 52. Stop guessing what to coach next.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/816c0f95-2140-4c1f-a020-5bf7f13475c5.jpg',
  },
  {
    title: 'The Coach’s Time-Saving Hacks',
    value: '$60 value',
    description:
      'Practical tips to reclaim 5 hours of your week and manage your academy like a pro.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8d8493ee-fa33-4fa1-a340-76e3a68f8ae1.jpg',
  },
]

export const packageItems = [
  '6 complete volumes',
  '5 strategic bonuses',
  '360+ ready-to-run sessions',
  '120+ drills & progressions',
  'All levels & categories',
  'Lifetime access',
]

export const testimonials: Testimonial[] = [
  {
    initials: 'MR',
    quote:
      'I mapped out my entire academy year in a single evening. Every session now has a clear tactical objective and my players are showing real progress.',
    name: 'Marc R.',
    role: 'Academy Head Coach · Barcelona, Spain',
  },
  {
    initials: 'SL',
    quote:
      'I no longer spend hours on YouTube hunting for drills. I open Tennis Ready and everything I need is right there. It is unbelievable.',
    name: 'Sarah L.',
    role: 'Teaching Pro · Florida, USA',
  },
  {
    initials: 'AP',
    quote:
      'My players improved faster in two months than in the entire previous season. The progressions simply make sense and they feel it on court.',
    name: 'Andrea P.',
    role: 'High Performance Coach · Rome, Italy',
  },
  {
    initials: 'DT',
    quote:
      'The assessment sheets completely changed my conversations with parents. I finally talk in numbers and progress, and they trust the process.',
    name: 'David T.',
    role: 'Club Director · Melbourne, Australia',
  },
  {
    initials: 'EW',
    quote:
      'As a first-year coach I was overwhelmed by group lessons. This gave me a full professional system from day one and my confidence doubled.',
    name: 'Emma W.',
    role: 'Junior Development Coach · Auckland, NZ',
  },
  {
    initials: 'RB',
    quote:
      'The rain-delay vault alone saved my winter season. Having ready-made plans at my fingertips is an absolute game changer.',
    name: 'Ryan B.',
    role: 'Head Coach · Dublin, Ireland',
  },
]

export const faqs: Faq[] = [
  {
    question: 'Is this suitable for beginner tennis coaches?',
    answer:
      'Yes. Tennis Ready™ gives first-year coaches a clear, professional system from day one while still offering depth for experienced high-performance coaches.',
  },
  {
    question: 'What levels and categories does this cover?',
    answer:
      'The system includes adaptable drills, sessions and progressions for every level, from beginner (9th category) and green ball through to high performance (1st category).',
  },
  {
    question: 'Does it work for private lessons and group clinics?',
    answer:
      'Both. Every session is built to scale, and the Group Coaching Blueprint covers rotations, stations and court flow so you can run large groups efficiently.',
  },
  {
    question: 'Is this a physical or digital product?',
    answer:
      'It is a 100% digital coaching resource. You get access to the complete package immediately and can use it on court from your phone or print what you need.',
  },
  {
    question: 'How do I receive my purchase?',
    answer:
      'After purchase, you receive instant access by email to all six volumes and five bonuses.',
  },
  {
    question: 'What if I am not satisfied?',
    answer:
      'You are protected by a 7-day money-back guarantee. Open everything, use the material and put it to the test on court; if it is not right for you, we will refund every cent.',
  },
  {
    question: 'How is this different from free drills online?',
    answer:
      'Free content is scattered and disconnected. Tennis Ready™ is a complete, organized coaching system with coherent progressions, evaluation tools and a full-season plan.',
  },
]

export const comparisonRows: ComparisonRow[] = [
  {
    without: '100h/year planning from scratch',
    withSystem: '10 min/week. Everything ready.',
  },
  {
    without: 'Improvised drills with no progression',
    withSystem: '120+ drills organized by level',
  },
  {
    without: 'Players improving slowly',
    withSystem: 'Logical, measurable progression',
  },
  {
    without: 'Parents asking for results',
    withSystem: 'Evaluation sheets and tracking',
  },
  {
    without: 'Walking on court unprepared',
    withSystem: 'Every session planned and ready',
  },
]

export const purchases: Purchase[] = [
  {
    name: 'Marc R.',
    location: 'Barcelona, Spain',
    product: 'TENNIS READY™ — Complete Package',
  },
  {
    name: 'Sarah L.',
    location: 'Florida, USA',
    product: 'TENNIS READY™ — Complete Package',
  },
  {
    name: 'David T.',
    location: 'Melbourne, Australia',
    product: 'TENNIS READY™ — Complete Package',
  },
  {
    name: 'Emma W.',
    location: 'Auckland, NZ',
    product: 'TENNIS READY™ — Complete Package',
  },
]
